<template>
  <div class="relative">
    <svg :viewBox="viewBox">
      <path class="background" :d="backgroundPath" :stroke="backgroundColor" :stroke-width="arcWidth">
      </path>
      <path v-for="(item, index) in graphSeries" :key="index"
            :stroke="item.color" :d="item.path" :stroke-width="arcWidth" >
      </path>
    </svg>
    <div class="absolute pin-t h-full w-full flex flex-col">
      <div class="flex flex-col justify-center items-center text-center h-full">
        <slot name="icon">

        </slot>
      </div>
    </div>
    <div class="justify-center text-center -mt-16">
      <slot name="text">
      </slot>
    </div>
  </div>
</template>

<script>
import { sortBy, reverse, map } from 'lodash'

export default {
  name: 'GaugeGraph',
  props: {
    series: Array,
    maximum: Number,
    arcWidth: {
      default: 2
    },
    graphSize: {
      default: 20
    },
    offset: {
      default: 135
    },
    arcSize: {
      default: 270
    },
    backgroundColor: {
      type: String,
      default: '#dfdfdf'
    }
  },
  data: () => {
    return {

    }
  },
  computed: {
    /*
     * Generates the size of the SVG based on the size
     */
    viewBox () {
      return `0 0 ${this.graphSize} ${this.graphSize}`
    },
    /*
     * Provides the background arc
     */
    backgroundPath () {
      return this.describeArc(
        this.graphDimensions.x,
        this.graphDimensions.y,
        this.graphDimensions.radius,
        0,
        this.arcSize
      )
    },
    /*
     * Provides the series data in a calculated format
     */
    graphSeries () {
      let series = sortBy(this.series, ['value'])
      series = reverse(series)

      return map(series, (item) => {
        return {
          color: item.color,
          path: this.calculateCurve(item.value)
        }
      })
    },
    /*
     * Generates an object with the size of the graph used for calculations
     */
    graphDimensions () {
      return {
        x: this.graphSize / 2,
        y: this.graphSize / 2,
        radius: (this.graphSize / 2) - this.arcWidth
      }
    }
  },
  methods: {
    /*
     * Does some maths mumbo-jumbo to calculate the arc around a central point
     */
    polarToCartesian (centerX, centerY, radius, angleInDegrees) {
      const angleInRadians = (angleInDegrees - 90) * Math.PI / 180.0

      return {
        x: centerX + (radius * Math.cos(angleInRadians)),
        y: centerY + (radius * Math.sin(angleInRadians))
      }
    },
    /*
     * Calculates the arc, the X and Y is the center position of the circle
     * The radius is the distance of the middle to the edge (should be smaller than
     * the X/Y values)
     * The start angle is 0 degrees
     * The end angle is 1-360 degrees
     *
     * Based off https://stackoverflow.com/a/18473154/795290
     */
    describeArc (x, y, radius, startAngle, endAngle) {
      const start = this.polarToCartesian(x, y, radius, (endAngle - this.offset))
      const end = this.polarToCartesian(x, y, radius, (startAngle - this.offset))

      const largeArcFlag = endAngle - startAngle <= 180 ? '0' : '1'

      return [
        'M', start.x, start.y,
        'A', radius, radius, 0, largeArcFlag, 0, end.x, end.y
      ].join(' ')
    },
    calculateCurve (value) {
      const percent = (Math.min(value, this.maximum) / this.maximum) // the size of the value compared to the maximum
      const curve = this.arcSize * percent // The size of the arc compared to the total
      return this.describeArc(
        this.graphDimensions.x,
        this.graphDimensions.y,
        this.graphDimensions.radius,
        0,
        curve
      )
    }
  }
}
</script>

<style scoped>
  svg path {
    fill: none;
    stroke-linecap: round;
    stroke-linejoin: round;
    transition: 0.2s;
  }
</style>
